import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Calendar as CalendarIcon, Clock, User, ChevronLeft, ChevronRight, CheckCircle2, Globe, Copy, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek, isToday, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getAvailableSlots } from '../utils/calendar';
import { CalendarEvent } from '../types';
import { notify } from '../lib/dialogs';

interface BookingLink {
    id: string;
    slug: string;
    name: string;
    description: string;
    duration_minutes: number;
    price: number;
    payment_type?: 'free' | 'pix' | 'external_link';
    payment_info?: string;
    company_id: number;
    user_id: string;
    working_hours: Record<string, { start: string, end: string }>;
    users?: { phone: string };
}

export function PublicBookingPage() {
    const { slug } = useParams();
    const [loading, setLoading] = useState(true);
    const [bookingDetails, setBookingDetails] = useState<BookingLink | null>(null);
    const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [availableSlots, setAvailableSlots] = useState<Date[]>([]);

    // Form states for booking
    const [patientName, setPatientName] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [patientPhone, setPatientPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const fetchBookingPage = async () => {
            if (!slug || !supabase) return;
            try {
                setLoading(true);
                const { data, error } = await supabase
                    .from('booking_links')
                    .select('*, users(phone)')
                    .eq('slug', slug)
                    .eq('is_active', true)
                    .single();

                if (error) throw error;
                if (data) {
                    setBookingDetails(data as BookingLink);
                }
            } catch (err) {
                console.error("Link de agendamento não encontrado", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBookingPage();
    }, [slug]);

    // Fetch Events when booking details or month changes
    useEffect(() => {
        const fetchEventsForMonth = async () => {
            if (!bookingDetails || !supabase) return;

            const startDate = startOfWeek(startOfMonth(currentMonth)).toISOString();
            const endDate = endOfWeek(endOfMonth(currentMonth)).toISOString();

            try {
                const { data, error } = await supabase
                    .from('events')
                    .select('*')
                    .eq('user_id', bookingDetails.user_id)
                    .gte('start_time', startDate)
                    .lte('end_time', endDate);

                if (error) throw error;
                setEvents(data as CalendarEvent[] || []);
            } catch (err) {
                console.error("Erro ao buscar eventos", err);
            }
        };

        if (bookingDetails) {
            fetchEventsForMonth();
        }
    }, [bookingDetails, currentMonth]);

    // Update available slots when selected date or events change
    useEffect(() => {
        if (!selectedDate || !bookingDetails) {
            setAvailableSlots([]);
            return;
        }

        const slots = getAvailableSlots(
            selectedDate,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            bookingDetails.working_hours as any,
            bookingDetails.duration_minutes,
            events
        );
        setAvailableSlots(slots);
    }, [selectedDate, events, bookingDetails]);

    const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

    const renderCalendarDays = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);
        const dateFormat = "d";
        const days = eachDayOfInterval({ start: startDate, end: endDate });

        return docsRow(days, monthStart, dateFormat);
    };

    const docsRow = (days: Date[], monthStart: Date, dateFormat: string) => {
        return days.map((day) => {
            const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
            const isPast = isBefore(day, startOfDay(new Date()));
            const isCurrentMonth = day.getMonth() === monthStart.getMonth();

            // Check if day has working hours configured
            const dayOfWeek = format(day, 'EEEE').toLowerCase();
            const hasWorkingHours = bookingDetails?.working_hours && Boolean(bookingDetails.working_hours[dayOfWeek]);

            const isSelectable = !isPast && hasWorkingHours && isCurrentMonth;

            return (
                <div
                    key={day.toString()}
                    onClick={() => {
                        if (isSelectable) {
                            setSelectedDate(day);
                            setSelectedSlot(null);
                        }
                    }}
                    className={`
                        p-2 w-10 h-10 mx-auto flex items-center justify-center rounded-full text-sm font-medium transition-all
                        ${!isCurrentMonth ? 'text-gray-300' : ''}
                        ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                        ${!hasWorkingHours && !isPast && isCurrentMonth ? 'text-gray-500 cursor-not-allowed bg-gray-50' : ''}
                        ${isSelectable && !isSelected ? 'cursor-pointer hover:bg-blue-50 text-gray-700' : ''}
                        ${isSelected ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30 font-bold' : ''}
                        ${isToday(day) && !isSelected ? 'text-blue-600 font-bold bg-blue-50/50' : ''}
                    `}
                >
                    {format(day, dateFormat)}
                </div>
            );
        });
    };

    const handleConfirmBooking = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSlot || !bookingDetails || !supabase) return;

        setIsSubmitting(true);
        try {
            const endTime = new Date(selectedSlot.getTime() + bookingDetails.duration_minutes * 60000);

            const { error } = await supabase
                .from('events')
                .insert([{
                    company_id: bookingDetails.company_id,
                    user_id: bookingDetails.user_id,
                    title: `Consulta: ${patientName}`,
                    start_time: selectedSlot.toISOString(),
                    end_time: endTime.toISOString(),
                    type: 'meeting',
                    booking_link_id: bookingDetails.id,
                    patient_name: patientName,
                    patient_email: patientEmail,
                    patient_phone: patientPhone,
                    status: bookingDetails.payment_type === 'pix' ? 'pending_payment' : 'confirmed'
                }]);

            if (error) throw error;

            setIsSuccess(true);
        } catch (error: unknown) {
            console.error('Error booking slot:', error);
            notify(`Erro ao agendar horário. Detalhes: ${(error instanceof Error ? error.message : String(error)) || JSON.stringify(error)}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!bookingDetails) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full">
                    <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Página não encontrada</h1>
                    <p className="text-gray-500">O link de agendamento que você acessou não existe ou foi desativado.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 flex flex-col md:flex-row min-h-[600px]">

                {/* Left Panel: Info */}
                <div className="w-full md:w-1/3 bg-gray-50/50 p-8 border-r border-gray-100 hidden md:block">
                    <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                        <CalendarIcon size={32} />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{bookingDetails.name}</h1>
                    {bookingDetails.description && (
                        <p className="text-gray-600 mb-6 leading-relaxed">{bookingDetails.description}</p>
                    )}

                    <div className="space-y-4 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                            <Clock className="w-5 h-5 text-blue-500" />
                            <span>{bookingDetails.duration_minutes} minutos</span>
                        </div>
                        {bookingDetails.price > 0 && (
                            <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                <span className="w-5 h-5 flex items-center justify-center text-emerald-500 border-2 border-emerald-500 rounded-full text-xs box-border">R$</span>
                                <span>R$ {bookingDetails.price.toFixed(2).replace('.', ',')}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel: Calendar vs Slots vs Form */}
                <div className="w-full md:w-2/3 p-6 md:p-8 flex flex-col">
                    {!selectedSlot && (
                        <>
                            <h2 className="text-xl font-bold text-gray-900 mb-6 font-display">Selecione uma Data</h2>

                            <div className="flex flex-col xl:flex-row gap-8">
                                {/* mini Calendar */}
                                <div className="flex-1 max-w-sm shrink-0">
                                    <div className="flex items-center justify-between mb-4">
                                        <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
                                            <ChevronLeft size={20} />
                                        </button>
                                        <h3 className="font-semibold text-gray-900 capitalize">
                                            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
                                        </h3>
                                        <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                                            <div key={i} className="text-center text-xs font-semibold text-gray-500 py-2">
                                                {day}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-7 gap-y-2 gap-x-1">
                                        {renderCalendarDays()}
                                    </div>
                                </div>

                                {/* Available Slots Column */}
                                <div className="flex-1 border-t xl:border-t-0 xl:border-l border-gray-100 pt-6 xl:pt-0 xl:pl-8">
                                    {selectedDate ? (
                                        <>
                                            <div className="flex justify-between items-end mb-4">
                                                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                                                    <span>{format(selectedDate, "eeee, d 'de' MMMM", { locale: ptBR })}</span>
                                                </h3>
                                                <div className="text-[11px] text-gray-500 flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md border border-gray-100" title="Horários exibidos no seu fuso horário local">
                                                    <Globe size={12} />
                                                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-2 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                                                {availableSlots.length > 0 ? (
                                                    availableSlots.map((slot, i) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => setSelectedSlot(slot)}
                                                            className="w-full py-3 px-4 rounded-xl border-2 border-blue-100 text-blue-700 font-medium hover:border-blue-600 hover:bg-blue-50 transition-all text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                        >
                                                            {format(slot, 'HH:mm')}
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-8">
                                                        <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500 font-medium">Nenhum horário disponível</p>
                                                        <p className="text-xs text-gray-500 mt-1">Tente selecionar outro dia.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-center py-12 xl:py-0">
                                            <CalendarIcon className="w-12 h-12 text-blue-100 mb-3" />
                                            <p className="text-gray-500 font-medium px-4">Escolha um dia no calendário ao lado para ver os horários livres.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {selectedSlot && !isSuccess && (
                        <div className="animate-fade-in">
                            <button
                                onClick={() => setSelectedSlot(null)}
                                className="text-sm font-medium text-gray-500 hover:text-gray-900 mb-6 flex items-center gap-1 transition-colors"
                            >
                                <ChevronLeft size={16} /> Voltar aos horários
                            </button>

                            <h2 className="text-xl font-bold text-gray-900 mb-2">Confirme seus dados</h2>
                            <p className="text-gray-500 mb-6">Você está agendando para <span className="font-semibold text-gray-900">{format(selectedSlot, "d 'de' MMMM 'às' HH:mm", { locale: ptBR })}</span></p>

                            <form onSubmit={handleConfirmBooking} className="space-y-4 max-w-sm">
                                <div>
                                    <label htmlFor="publicbookingpage-nome-completo" className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                                    <input id="publicbookingpage-nome-completo"
                                        type="text"
                                        required
                                        value={patientName}
                                        onChange={e => setPatientName(e.target.value)}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors text-gray-900"
                                        placeholder="Maria Silva"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="publicbookingpage-e-mail" className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                                    <input id="publicbookingpage-e-mail"
                                        type="email"
                                        required
                                        value={patientEmail}
                                        onChange={e => setPatientEmail(e.target.value)}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors text-gray-900"
                                        placeholder="maria@exemplo.com"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label htmlFor="publicbookingpage-whatsapp-telefone" className="block text-sm font-medium text-gray-700 mb-1">WhatsApp / Telefone</label>
                                    <input id="publicbookingpage-whatsapp-telefone"
                                        type="tel"
                                        required
                                        value={patientPhone}
                                        onChange={e => setPatientPhone(e.target.value)}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors text-gray-900"
                                        placeholder="(11) 99999-9999"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Agendando...' : 'Confirmar Agendamento'}
                                </button>
                            </form>
                        </div>
                    )}

                    {isSuccess && (
                        <div className="flex flex-col items-center justify-center text-center h-full animate-fade-in space-y-4 px-4">
                            <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-2">
                                <CheckCircle2 size={32} />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">
                                {bookingDetails.payment_type === 'pix' ? 'Quase lá!' : 'Agendamento Confirmado!'}
                            </h2>
                            <p className="text-gray-500 max-w-md">
                                {bookingDetails.payment_type === 'pix' ? (
                                    <>
                                        Seu horário para <strong>{format(selectedSlot!, "d 'de' MMMM 'às' HH:mm", { locale: ptBR })}</strong> foi pré-reservado.
                                        Para confirmar definitivamente, realize o pagamento via Pix.
                                    </>
                                ) : (
                                    <>
                                        Tudo certo, <strong>{patientName}</strong>! Sua marcação para <strong>{format(selectedSlot!, "d 'de' MMMM 'às' HH:mm", { locale: ptBR })}</strong> foi confirmada.
                                    </>
                                )}
                            </p>

                            {bookingDetails.payment_type === 'pix' && (
                                <div className="mt-8 w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                                    <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center">
                                        <h3 className="text-xl font-bold mb-1">Pagamento via Pix</h3>
                                        <p className="text-teal-50 text-sm opacity-90">Rápido, prático e seguro.</p>
                                    </div>
                                    
                                    <div className="p-6">
                                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                                            <span className="text-gray-500 font-medium">Valor da Consulta</span>
                                            <span className="text-3xl font-black text-gray-900">R$ {bookingDetails.price.toFixed(2).replace('.', ',')}</span>
                                        </div>

                                        <div className="mb-6">
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                Chave Pix (Copie e Cole)
                                            </label>
                                            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-xl border border-gray-200">
                                                <code className="flex-1 px-3 text-gray-800 font-mono font-medium truncate">
                                                    {bookingDetails.payment_info}
                                                </code>
                                                <button 
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(bookingDetails.payment_info || '');
                                                        toast.success('Chave copiada com sucesso!');
                                                    }}
                                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg shadow-sm transition-all font-medium text-sm"
                                                >
                                                    <Copy size={16} /> Copiar
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-amber-50 rounded-xl p-4 mb-6 border border-amber-100">
                                            <p className="text-sm text-amber-800 leading-relaxed text-left">
                                                Após realizar a transferência, é <strong>obrigatório</strong> enviar o comprovante pelo WhatsApp para que o seu agendamento seja definitivamente confirmado.
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                const formattedTime = format(selectedSlot!, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
                                                const msg = `Olá! Acabei de fazer um agendamento para ${formattedTime} no nome de ${patientName}. Segue o comprovante do Pix!`;
                                                const phoneStr = bookingDetails.users?.phone ? bookingDetails.users.phone.replace(/\D/g, '') : '';
                                                const waLink = phoneStr 
                                                    ? `https://wa.me/55${phoneStr}?text=${encodeURIComponent(msg)}`
                                                    : `https://wa.me/?text=${encodeURIComponent(msg)}`;
                                                window.open(waLink, '_blank');
                                            }}
                                            className="w-full flex items-center justify-center gap-2 py-4 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-xl shadow-md shadow-green-500/20 transition-all focus:ring-4 focus:ring-green-500/30"
                                        >
                                            <MessageCircle size={22} />
                                            Enviar Comprovante no WhatsApp
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
